import { Trans, useLingui } from "@lingui/react/macro";
import type { LinkFavicons } from "@rakazo/chat-ui/web";
import { LinkFaviconsContext, RemoteImagesContext } from "@rakazo/chat-ui/web";
import { analyticsScreen } from "@rakazo/core";
import { Button } from "@rakazo/ui-web";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { Location } from "react-router-dom";
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { LoadingState } from "./components/ai/primitives";
import { DesktopDocumentOpener } from "./components/DesktopDocumentOpener";
import { EchoProvider } from "./components/EchoProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NativeEditorDocuments } from "./components/NativeEditorDocuments";
import { RouteLoadingState } from "./components/RouteLoadingState";
import { ScreenLockGate } from "./components/ScreenLock";
import { SubscriptionGate } from "./components/SubscriptionGate";
import { WorkspaceLifetime } from "./components/WorkspaceLifetime";
import { WorkspaceNotificationInbox } from "./components/WorkspaceNotifications";
import { WorkspaceSession } from "./components/WorkspaceSession";
import { authClient } from "./lib/auth";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "./lib/bootstrap";
import { desktopBridge } from "./lib/desktop";
import { loadBriefingPage, loadEditorPage, loadFilesPage } from "./lib/home-page-loaders";
import { identifyMonitoringUser } from "./lib/monitoring";
import { markAfterPaint, markOnce } from "./lib/performance";
import { lazyComponent } from "./lib/preload-recovery";
import { getRemoteImagesEnabled, subscribeRemoteImages } from "./lib/remote-images-preference";
import { rpc, selectedSpaceId, subscribeSelectedSpace } from "./lib/rpc";
import { safeNextPath } from "./lib/safe-next-path";
import {
  holdAnonymousGate,
  holdUnreachableGate,
  sessionGate,
  sessionRetryDelayMs,
  showSessionUnavailable,
} from "./lib/session-gate";
import { completeSsoCallback, SSO_CALLBACK_PATH } from "./lib/sso-flow";
import { WorkspaceScopeContext } from "./lib/workspace-local-state";
import { CallAudioPage } from "./pages/CallAudio";
import { IntegrationSetupPage } from "./pages/IntegrationSetup";
import { McpOAuthCallbackPage } from "./pages/McpOAuthCallback";
import { SharedBotsPage } from "./pages/SharedBots";
import { SharedChatPage } from "./pages/SharedChat";
import { ShellPage } from "./pages/Shell";
import { TasksPage } from "./pages/Tasks";

const WorkspacePage = lazyComponent(() =>
  import("./pages/Workspace").then((module) => module.WorkspacePage),
);
const ContinuePage = lazyComponent(() =>
  import("./pages/Continue").then((module) => module.ContinuePage),
);
const HomePage = lazyComponent(() => import("./pages/Home").then((module) => module.HomePage));
const TextEditorPage = lazyComponent(loadEditorPage);
const MissionBriefingPage = lazyComponent(loadBriefingPage);

const AuthPage = lazyComponent(() => import("./pages/Auth").then((module) => module.AuthPage));
const PasswordResetPage = lazyComponent(() =>
  import("./pages/Auth").then((module) => module.PasswordResetPage),
);
const OnboardingPage = lazyComponent(() =>
  import("./pages/Onboarding").then((module) => module.OnboardingPage),
);
const WelcomePage = lazyComponent(() =>
  import("./pages/Welcome").then((module) => module.WelcomePage),
);
const ArtifactsPage = lazyComponent(loadFilesPage);
const MissionPreviewPage = lazyComponent(() =>
  import("./pages/MissionPreview").then((module) => module.MissionPreviewPage),
);
const MissionsPage = lazyComponent(() =>
  import("./pages/Missions").then((module) => module.MissionsPage),
);
const ProjectsPage = lazyComponent(() =>
  import("./pages/Projects").then((module) => module.ProjectsPage),
);

function SsoCallbackPage() {
  useEffect(() => {
    completeSsoCallback();
  }, []);
  return null;
}

// Link icons come from our API, which fetches and caches them; the browser never asks the site.
const linkFavicons: LinkFavicons = {
  load: (origin) => rpc.links.favicon({ origin }),
};

export function App() {
  if (window.location.pathname === "/call-audio") return <CallAudioPage />;
  if (window.location.pathname === "/shared" || /^\/shared\/[a-z0-9]+(?:-[a-z0-9]+)*\/?$/.test(window.location.pathname)) return <SharedChatPage />;
  return <PrivateApp />;
}

function PrivateApp() {
  const loadRemoteImages = useSyncExternalStore(
    subscribeRemoteImages,
    getRemoteImagesEnabled,
    () => false,
  );
  return (
    <RemoteImagesContext.Provider value={loadRemoteImages}>
      <LinkFaviconsContext.Provider value={linkFavicons}>
        <ErrorBoundary fallback={<AppFailed />}>
          {window.location.pathname === SSO_CALLBACK_PATH ? <SsoCallbackPage /> : <SessionApp />}
        </ErrorBoundary>
      </LinkFaviconsContext.Provider>
    </RemoteImagesContext.Provider>
  );
}

function SessionApp() {
  const spaceId = useSyncExternalStore(subscribeSelectedSpace, selectedSpaceId, () => null);
  const [searchParams] = useSearchParams();
  const signInDestination = safeNextPath(searchParams.get("next")) ?? "/app";
  const ticketBoardEnabled = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  const session = authClient.useSession();
  const location = useLocation();
  const pathname = location.pathname;
  const screen = analyticsScreen(pathname);
  const analyticsUserId = session.data?.user.id;
  const lastScreen = useRef<string | undefined>(undefined);
  useEffect(() => {
    // A pending session is not a sign-out; startup already tagged the signed-in user.
    if (session.isPending) return;
    void identifyMonitoringUser(Boolean(analyticsUserId));
  }, [analyticsUserId, session.isPending]);
  useEffect(() => {
    if (!analyticsUserId) {
      lastScreen.current = undefined;
      return;
    }
    if (!screen) return;
    const key = `${analyticsUserId}:${screen}`;
    if (lastScreen.current === key) return;
    lastScreen.current = key;
    void Promise.resolve()
      .then(() => rpc.analytics.screen({ screen, surface: desktopBridge() ? "desktop" : "web" }))
      .catch(() => undefined);
  }, [analyticsUserId, screen]);
  const gate = sessionGate(session);
  const [holdingUnreachable, setHoldingUnreachable] = useState(false);
  const nextHolding = holdUnreachableGate(gate, holdingUnreachable);
  if (nextHolding !== holdingUnreachable) setHoldingUnreachable(nextHolding);
  const [holdingAnonymous, setHoldingAnonymous] = useState(false);
  const nextHoldingAnonymous = holdAnonymousGate(gate, holdingAnonymous);
  if (nextHoldingAnonymous !== holdingAnonymous) setHoldingAnonymous(nextHoldingAnonymous);

  useLayoutEffect(() => {
    if (session.isPending) return;
    markOnce("rk:renderer:session-committed");
    markAfterPaint("rk:renderer:session-painted");
  }, [session.isPending]);

  if (showSessionUnavailable(gate, nextHolding)) {
    return <SessionUnavailable refetch={session.refetch} />;
  }
  if (gate === "loading" && !nextHoldingAnonymous) {
    return pathname.startsWith("/app") ? (
      <RouteLoadingState />
    ) : (
      <div
        className="grid h-full place-items-center text-muted-foreground/80"
        data-rakazo-app-state="session-pending"
      >
        <Trans>Loading…</Trans>
      </div>
    );
  }

  const user = session.data?.user;
  return (
    <div className="h-full" data-rakazo-app-state="ready">
      <Suspense fallback={<RouteLoadingState />}>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/app" replace /> : <WelcomePage />} />
          <Route
            path="/sign-in"
            element={
              user ? <Navigate to={signInDestination} replace /> : <AuthPage key="in" mode="in" />
            }
          />
          <Route
            path="/sign-up"
            element={user ? <Navigate to="/onboarding" replace /> : <AuthPage key="up" mode="up" />}
          />
          <Route
            path="/forgot-password"
            element={
              user ? <Navigate to="/app" replace /> : <AuthPage key="forgot" mode="forgot" />
            }
          />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route
            path="/mcp/oauth/callback"
            element={user ? <McpOAuthCallbackPage /> : <Navigate to="/sign-in" replace />}
          />
          <Route
            path="/integrations/setup"
            element={
              user ? (
                <IntegrationSetupPage />
              ) : (
                <Navigate to="/sign-in?next=/integrations/setup" replace />
              )
            }
          />
          <Route
            element={
              user ? (
                <ScreenLockGate key={user.id}>
                  <SubscriptionGate fallback={<GateFallback />}>
                    <EchoProvider>
                      <WorkspaceScopeContext.Provider
                        value={JSON.stringify([user.id, spaceId])}
                        key={JSON.stringify([user.id, spaceId])}
                      >
                        <WorkspaceLifetime>
                          <NativeEditorDocuments>
                            <DesktopDocumentOpener />
                            <Outlet />
                          </NativeEditorDocuments>
                        </WorkspaceLifetime>
                      </WorkspaceScopeContext.Provider>
                    </EchoProvider>
                  </SubscriptionGate>
                </ScreenLockGate>
              ) : (
                <Navigate
                  to={
                    safeNextPath(`${location.pathname}${location.search}`)
                      ? `/sign-in?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`
                      : "/sign-in"
                  }
                  replace
                />
              )
            }
          >
            <Route path="/continue" element={<ContinuePage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route
              path="/app/*"
              element={
                import.meta.env.VITE_WORKSPACE_ENABLED === "1" ? (
                  <WorkspaceSession
                    render={(location) => (
                      <WorkspaceRoutes
                        location={location}
                        ticketBoardEnabled={ticketBoardEnabled}
                      />
                    )}
                  />
                ) : (
                  <WorkspaceRoutes ticketBoardEnabled={ticketBoardEnabled} />
                )
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}

/**
 * A session lookup that never reached the server is not a sign-out, so the app
 * waits and retries here instead of routing to sign-in and stranding a signed-in
 * user. Better Auth only polls once a session exists, so the retry lives here.
 */
function SessionUnavailable({ refetch }: { refetch: () => Promise<void> }) {
  const { t } = useLingui();
  const [attempt, setAttempt] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const retryImmediately = useRef(false);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    let cancelled = false;
    const delay = retryImmediately.current ? 0 : sessionRetryDelayMs(attempt);
    retryImmediately.current = false;
    const timer = setTimeout(() => {
      void refetchRef.current().finally(() => {
        if (!cancelled) setAttempt((value) => value + 1);
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [attempt, retryKey]);

  return (
    <div className="grid h-full place-items-center bg-background px-6 text-center">
      <div className="flex flex-col items-center">
        <LoadingState label={t`Reconnecting`} />
        <p className="mt-3 text-[13.5px] text-muted-foreground/80">
          <Trans>Can&apos;t reach the server.</Trans>
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            className="rounded-full"
            onClick={() => {
              retryImmediately.current = true;
              setAttempt(0);
              setRetryKey((key) => key + 1);
            }}
          >
            <Trans>Retry now</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Last resort for a render failure no inner boundary caught: offer a reload instead of a blank page. */
function AppFailed() {
  return (
    <div
      className="grid h-full place-items-center bg-background px-6 text-center"
      data-rakazo-app-state="failed"
    >
      <div className="flex flex-col items-center">
        <p className="text-[13.5px] text-muted-foreground/80">
          <Trans>Something went wrong. Try again.</Trans>
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            className="rounded-full"
            onClick={() => window.location.reload()}
          >
            <Trans>Refresh</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

function GateFallback() {
  return <RouteLoadingState />;
}

function WorkspaceRoutes({
  location,
  ticketBoardEnabled,
}: {
  location?: Location;
  ticketBoardEnabled: boolean;
}) {
  return (
    <Routes location={location}>
      <Route path="/" element={<HomePage />} />
      <Route path="projects" element={<ProjectsPage />} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="missions" element={<MissionsPage />} />
      <Route path="mission-preview" element={<MissionPreviewPage />} />
      <Route path="workspace/:section" element={<WorkspacePage />} />
      <Route path="messages" element={<ShellPage />} />
      <Route path="notifications" element={<WorkspaceNotificationInbox />} />
      <Route path="editor" element={<TextEditorPage />} />
      <Route path="mission-briefing" element={<MissionBriefingPage />} />
      <Route path="shared-bots" element={<SharedBotsPage />} />
      <Route path="g/:groupId" element={<ShellPage />} />
      <Route path="artifacts" element={<ArtifactsPage />} />
      <Route path="artifacts/:artifactId" element={<ArtifactsPage />} />
      {ticketBoardEnabled ? (
        <Route path="board" element={<Navigate to="/app/projects" replace />} />
      ) : null}
      <Route path=":botId" element={<ShellPage />} />
    </Routes>
  );
}
